import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "./supabase";
import { setSupabaseTenantId } from "./tenantTransport";
import type { User, Session } from "@supabase/supabase-js";
import {
  normalizeAuthoritativeMemberships,
  normalizeSelectedTenant,
  parseProviderAdmin,
  PENDING_TENANT_RNC_STORAGE_KEY,
  selectAuthoritativeMembership,
  shouldUseLegacyProviderCompatibility,
  TENANT_PREFERENCE_STORAGE_KEY,
  type AuthoritativeTenantMembership,
  type UserRole,
} from "./authoritativeTenant";

export type { UserRole } from "./authoritativeTenant";

// Antigravity Sovereign RBAC — Enterprise Role Hierarchy
// super_admin: OsirisBrain platform owner — creates tenants, full system access
// tenant_admin: Full access to their tenant — manages users, settings, everything
// manager: Dashboard, sales, inventory, reports, HR, expenses — no system settings
// accountant: Financial: reports, expenses, taxes, accounting, reconciliation, AP
// hr: Human resources and payroll only
// sales: POS, customers, invoices, promotions, CRM
// technician: Installations, warranties, service orders
// supervisor: Sales + inventory + reports oversight
// cashier: POS terminal and cash shifts only
// Route permission map — granular access per role
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  super_admin: ["*"], // Wildcard — everything
  tenant_admin: ["*"], // Full access to their tenant
  manager: [
    "/",
    "/pos/visual",
    "/pos/linear",
    "/hub/customers",
    "/hub/crm",
    "/hub/service-orders",
    "/hub/workshop",
    "/hub/hotel",
    "/hub/clinic",
    "/hub/gym",
    "/hub/construction",
    "/hub/education",
    "/hub/realestate",
    "/hub/veterinary",
    "/hub/dental",
    "/hub/florist",
    "/hub/car-dealer",
    "/hub/funeral",
    "/hub/photo-studio",
    "/hub/inventory-tracking",
    "/hub/ecommerce-manager",
    "/hub/payment-gateway",
    "/hub/odontogram",
    "/hub/photo-gallery",
    "/hub/test-drive",
    "/inventory/overview",
    "/hub/reports",
    "/hub/taxes",
    "/hub/expenses",
    "/hub/suppliers",
    "/hub/purchase-orders",
    "/hub/accounts-payable",
    "/hub/credit-accounts",
    "/hub/promotions",
    "/hub/cash-shifts",
    "/hub/delivery",
    "/hub/kds",
    "/hub/reservations",
    "/hub/warranties",
    "/hub/installations",
    "/hub/branches",
    "/hub/print-invoice",
    "/hub/bi-analytics",
    "/hub/commissions",
    "/hub/accounting",
    "/hub/ledger",
    "/hub/fixed-assets",
    "/hub/period-closing",
    "/ai/reconciliation",
    "/sales/transactions",
    "/sales/products",
    "/hub/receive-payment",
    "/hub/advanced-payroll",
    "/hub/purchase-requisitions",
    "/hub/banking",
    "/hub/multi-currency",
    "/hub/service-contracts",
    "/employee-portal",
  ],
  accountant: [
    "/",
    "/hub/reports",
    "/hub/taxes",
    "/hub/expenses",
    "/hub/suppliers",
    "/hub/accounting",
    "/hub/ledger",
    "/hub/fixed-assets",
    "/hub/period-closing",
    "/ai/reconciliation",
    "/hub/purchase-orders",
    "/hub/purchase-requisitions",
    "/hub/accounts-payable",
    "/hub/credit-accounts",
    "/hub/print-invoice",
    "/hub/bi-analytics",
    "/hub/multi-currency",
    "/hub/banking",
    "/hub/advanced-payroll",
    "/sales/transactions",
    "/hub/receive-payment",
    "/employee-portal",
  ],
  hr: [
    "/",
    "/hub/hr",
    "/hub/advanced-payroll",
    "/hub/commissions",
    "/employee-portal",
  ],
  sales: [
    "/pos/linear",
    "/pos/visual",
    "/hub/customers",
    "/hub/crm",
    "/hub/service-orders",
    "/hub/workshop",
    "/hub/hotel",
    "/hub/clinic",
    "/hub/gym",
    "/hub/construction",
    "/hub/education",
    "/hub/realestate",
    "/hub/veterinary",
    "/hub/dental",
    "/hub/florist",
    "/hub/car-dealer",
    "/hub/funeral",
    "/hub/photo-studio",
    "/hub/inventory-tracking",
    "/hub/ecommerce-manager",
    "/hub/payment-gateway",
    "/hub/odontogram",
    "/hub/photo-gallery",
    "/hub/test-drive",
    "/inventory/overview",
    "/hub/promotions",
    "/hub/print-invoice",
    "/hub/commissions",
    "/hub/credit-accounts",
    "/hub/reservations",
    "/sales/products",
    "/hub/receive-payment",
    "/customer-portal",
  ],
  technician: [
    "/",
    "/hub/installations",
    "/hub/warranties",
    "/hub/service-orders",
    "/hub/workshop",
    "/hub/service-contracts",
  ],
  supervisor: [
    "/",
    "/pos/visual",
    "/pos/linear",
    "/hub/customers",
    "/inventory/overview",
    "/hub/crm",
    "/hub/reports",
    "/hub/expenses",
    "/hub/suppliers",
    "/hub/purchase-orders",
    "/hub/accounts-payable",
    "/hub/promotions",
    "/hub/cash-shifts",
    "/hub/service-orders",
    "/hub/print-invoice",
    "/hub/commissions",
    "/hub/purchase-requisitions",
    "/sales/transactions",
    "/sales/products",
    "/hub/receive-payment",
    "/hub/credit-accounts",
  ],
  cashier: [
    "/",
    "/pos/visual",
    "/hub/cash-shifts",
    "/hub/receive-payment",
    "/employee-portal",
  ],
};

// The "Zero Fallback" mandate means if there's no context, things break by design
interface TenantContextType {
  user: User | null;
  tenantId: string | null;
  tenantName: string | null;
  businessType: string | null;
  isSuperAdmin: boolean;
  isLegacyProviderAuth: boolean;
  userRole: UserRole | null;
  availableTenants: AuthoritativeTenantMembership[];
  tenantRevision: number;
  loading: boolean;
  signOut: () => Promise<void>;
  switchTenant: (tenantId: string) => void;
  canAccess: (route: string) => boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [businessType, setBusinessType] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLegacyProviderAuth, setIsLegacyProviderAuth] = useState(false);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [availableTenants, setAvailableTenants] = useState<
    AuthoritativeTenantMembership[]
  >([]);
  const [tenantRevision, setTenantRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const requestGenerationRef = useRef(0);

  const clearPublishedContext = useCallback(
    (removePreference: boolean) => {
      setSupabaseTenantId(null);
      setUser(null);
      setTenantId(null);
      setTenantName(null);
      setBusinessType(null);
      setIsSuperAdmin(false);
      setIsLegacyProviderAuth(false);
      setUserRole(null);
      setAvailableTenants([]);
      if (removePreference) {
        localStorage.removeItem(TENANT_PREFERENCE_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_TENANT_RNC_STORAGE_KEY);
      }
      setTenantRevision((current) => current + 1);
    },
    [],
  );

  const handleSession = useCallback(async (session: Session | null) => {
    const requestGeneration = requestGenerationRef.current + 1;
    requestGenerationRef.current = requestGeneration;
    setLoading(true);
    setSupabaseTenantId(null);
    setUser(null);
    setTenantId(null);
    setTenantName(null);
    setBusinessType(null);
    setIsSuperAdmin(false);
    setIsLegacyProviderAuth(false);
    setUserRole(null);
    setAvailableTenants([]);

    if (!session?.user) {
      localStorage.removeItem(TENANT_PREFERENCE_STORAGE_KEY);
      sessionStorage.removeItem(PENDING_TENANT_RNC_STORAGE_KEY);
      setTenantRevision((current) => current + 1);
      setLoading(false);
      return;
    }

    try {
      const legacyProvider = shouldUseLegacyProviderCompatibility(
        session.user.app_metadata,
        import.meta.env.VITE_LEGACY_PROVIDER_COMPAT === "true",
      );
      const providerResult = await supabase.rpc(
        "is_current_provider_admin",
      );
      if (requestGeneration !== requestGenerationRef.current) return;

      let isProvider = false;
      if (providerResult.error) {
        if (!legacyProvider) throw providerResult.error;
      } else {
        isProvider = parseProviderAdmin(providerResult.data);
      }

      if (isProvider || legacyProvider) {
        setSupabaseTenantId(null);
        setUser(session.user);
        setTenantId(null);
        setTenantName("OsirisBrain Global Central");
        setBusinessType("general");
        setIsSuperAdmin(true);
        setIsLegacyProviderAuth(!isProvider && legacyProvider);
        setUserRole("super_admin");
        setAvailableTenants([]);
        localStorage.removeItem(TENANT_PREFERENCE_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_TENANT_RNC_STORAGE_KEY);
        setTenantRevision((current) => current + 1);
        return;
      }

      const membershipResult = await supabase.rpc("list_my_tenants");
      if (requestGeneration !== requestGenerationRef.current) return;
      if (membershipResult.error) throw membershipResult.error;
      const memberships = normalizeAuthoritativeMemberships(
        membershipResult.data,
      );

      let selected: AuthoritativeTenantMembership;
      const pendingRnc = sessionStorage.getItem(
        PENDING_TENANT_RNC_STORAGE_KEY,
      );
      if (pendingRnc) {
        const selectionResult = await supabase.rpc(
          "select_tenant_for_session",
          { target_rnc: pendingRnc },
        );
        if (requestGeneration !== requestGenerationRef.current) return;
        if (selectionResult.error) throw selectionResult.error;
        selected = normalizeSelectedTenant(
          selectionResult.data,
          memberships,
        );
      } else {
        selected = selectAuthoritativeMembership(
          memberships,
          localStorage.getItem(TENANT_PREFERENCE_STORAGE_KEY),
        );
      }

      if (requestGeneration !== requestGenerationRef.current) return;
      setSupabaseTenantId(selected.tenantId);
      setUser(session.user);
      setTenantId(selected.tenantId);
      setTenantName(selected.tenantName);
      setBusinessType(selected.businessType);
      setIsSuperAdmin(false);
      setIsLegacyProviderAuth(false);
      setUserRole(selected.role);
      setAvailableTenants(memberships);
      localStorage.setItem(
        TENANT_PREFERENCE_STORAGE_KEY,
        selected.tenantId,
      );
      sessionStorage.removeItem(PENDING_TENANT_RNC_STORAGE_KEY);
      setTenantRevision((current) => current + 1);
    } catch (err) {
      console.error(
        "CRITICAL FATAL: Could not establish Sovereign Tenant context.",
        err,
      );
      if (requestGeneration === requestGenerationRef.current) {
        clearPublishedContext(true);
        try {
          await supabase.auth.signOut({ scope: "local" });
        } catch (signOutError) {
          console.error(
            "CRITICAL FATAL: Could not clear the invalid local session.",
            signOutError,
          );
        }
      }
    } finally {
      if (requestGeneration === requestGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [clearPublishedContext]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      void handleSession(session);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (
          event !== "SIGNED_IN" &&
          event !== "SIGNED_OUT" &&
          event !== "USER_UPDATED"
        ) {
          return;
        }
        queueMicrotask(() => {
          void handleSession(session);
        });
      },
    );

    return () => {
      requestGenerationRef.current += 1;
      authListener.subscription.unsubscribe();
    };
  }, [handleSession]);

  const signOut = useCallback(async () => {
    requestGenerationRef.current += 1;
    clearPublishedContext(true);
    setLoading(false);
    await supabase.auth.signOut();
  }, [clearPublishedContext]);

  const switchTenant = useCallback(
    (targetTenantId: string) => {
      const selected = selectAuthoritativeMembership(
        availableTenants,
        targetTenantId,
      );
      requestGenerationRef.current += 1;
      setSupabaseTenantId(selected.tenantId);
      setTenantId(selected.tenantId);
      setTenantName(selected.tenantName);
      setBusinessType(selected.businessType);
      setUserRole(selected.role);
      localStorage.setItem(
        TENANT_PREFERENCE_STORAGE_KEY,
        selected.tenantId,
      );
      setTenantRevision((current) => current + 1);
    },
    [availableTenants],
  );

  const canAccess = useCallback(
    (route: string): boolean => {
      if (isSuperAdmin) return true;
      if (!userRole) return false;
      const perms = ROLE_PERMISSIONS[userRole];
      if (!perms) return false;
      if (perms.includes("*")) return true;
      return perms.includes(route);
    },
    [isSuperAdmin, userRole],
  );

  const value = useMemo(
    () => ({
      user,
      tenantId,
      tenantName,
      businessType,
      isSuperAdmin,
      isLegacyProviderAuth,
      userRole,
      availableTenants,
      tenantRevision,
      loading,
      signOut,
      switchTenant,
      canAccess,
    }),
    [
      user,
      tenantId,
      tenantName,
      businessType,
      isSuperAdmin,
      isLegacyProviderAuth,
      userRole,
      availableTenants,
      tenantRevision,
      loading,
      signOut,
      switchTenant,
      canAccess,
    ],
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};
